import {
  EEmailStatus,
  ELoopingAudio,
  EOutboundPhoneStatus,
  EPhoneStatus,
  ETextStatus,
  TChatbotSettingsLayout,
  TCustomInstruction,
  TEscalation,
  TLink,
} from '~/features/settings/models'
import { TUser } from '~/features/user/models'
import {
  EAgentNotifyWhen,
  ECTAFriendly,
  ELocationTypeValues,
  ENotificationType,
  EPersonalityFriendlyName,
  EPersonalityValues,
  EScheduleValues,
  ETextMethodValues,
} from '~/models/features'

export enum EWebScrapeStatus {
  unloaded = 'unloaded',
  started = 'Started',
  loaded = 'Loaded',
  failed = 'Failed',
}

export enum EChatStatus {
  active = 'active',
  paused = 'paused',
  disabled = 'disabled',
}

export type TWebScrapeStatus = {
  scrapeStatus: EWebScrapeStatus
}

export type TOrg = {
  id: string
  createdDate: Date
  agents: TAgent[]
  users: TUser[]
}
export enum EAgentKeys {
  id = 'id',
  agentReferenceName = 'agentReferenceName',
  agentEmail = 'agentEmail',
  emailStatus = 'emailStatus',
  agentName = 'agentName',
  orgId = 'orgId',
  Org = 'Org',
  businessName = 'businessName',
  service = 'service',
  siteLink = 'siteLink',
  locationType = 'locationType',
  location = 'location',
  pricing = 'pricing',
  questions = 'questions',
  facts = 'facts',
  calendlyLink = 'calendlyLink',
  cta = 'cta',
  ctaText = 'ctaText',
  customURL = 'customURL',
  emailConsent = 'emailConsent',
  mobileConsent = 'mobileConsent',
  notifyEmail = 'notifyEmail',
  notifyMobile = 'notifyMobile',
  notifyType = 'notifyType',
  notifyWhen = 'notifyWhen',
  personality = 'personality',
  createdDate = 'createdDate',
  updatedDate = 'updatedDate',
  phoneIntro = 'phoneIntro',
  phoneNumber = 'phoneNumber',
  agentLanguage = 'agentLanguage',
  logoImgUrl = 'logoImgUrl',
  scheduleType = 'scheduleType',
  zapierId = 'zapierId',
  customLinks = 'customLinks',
  chatIntro = 'chatIntro',
  chatbotImgURL = 'chatbotImgURL',
  showLibby = 'showLibby',
  showBranding = 'showBranding',
  chatFriendlyUrl = 'chatFriendlyUrl',
  phoneStatus = 'phoneStatus',
  phoneVoice = 'phoneVoice',
  phoneIntroRecording = 'phoneIntroRecording',
  textMethod = 'textMethod',
  scrapeStatus = 'scrapeStatus',
  customInstructions = 'customInstructions',
  escalations = 'escalations',
  chatStatus = 'chatStatus',
  textStatus = 'textStatus',
  loopingAudio = 'loopingAudio',
  agentEmailCustom = 'agentEmailCustom',
  outboundPhoneStatus = 'outboundPhoneStatus',
  agentTimezone = 'agentTimezone',
  customAgentRules = 'customAgentRules',
}

export type TAgentFact =
  | string
  | {
      id?: string
      text: string
    }

export type TAgent = {
  [EAgentKeys.id]: string
  [EAgentKeys.agentEmail]?: string | null
  [EAgentKeys.agentReferenceName]?: string | null
  [EAgentKeys.emailStatus]?: EEmailStatus
  [EAgentKeys.agentName]?: string
  [EAgentKeys.orgId]?: string
  [EAgentKeys.Org]?: TOrg
  [EAgentKeys.businessName]: string
  [EAgentKeys.service]: string
  [EAgentKeys.siteLink]?: string
  [EAgentKeys.locationType]?: ELocationTypeValues
  [EAgentKeys.location]?: string
  [EAgentKeys.pricing]?: string
  [EAgentKeys.questions]?: string[]
  [EAgentKeys.facts]?: TAgentFact[]
  [EAgentKeys.calendlyLink]?: string
  [EAgentKeys.cta]?: ECTAFriendly
  [EAgentKeys.ctaText]?: string | null
  [EAgentKeys.customURL]?: TLink[] | string
  [EAgentKeys.emailConsent]?: string
  [EAgentKeys.mobileConsent]?: boolean
  [EAgentKeys.notifyEmail]?: string
  [EAgentKeys.notifyMobile]?: string
  [EAgentKeys.notifyType]?: ENotificationType
  [EAgentKeys.notifyWhen]?: EAgentNotifyWhen
  [EAgentKeys.personality]?: EPersonalityValues
  [EAgentKeys.createdDate]?: Date
  [EAgentKeys.updatedDate]?: Date
  [EAgentKeys.phoneIntro]?: string
  [EAgentKeys.phoneNumber]?: string
  [EAgentKeys.agentLanguage]?: string
  [EAgentKeys.logoImgUrl]?: string | null
  [EAgentKeys.scheduleType]?: EScheduleValues
  [EAgentKeys.zapierId]?: string
  [EAgentKeys.customLinks]?: TLink[]
  [EAgentKeys.chatIntro]?: string
  [EAgentKeys.chatbotImgURL]?: string
  [EAgentKeys.showLibby]?: boolean
  [EAgentKeys.showBranding]?: boolean
  [EAgentKeys.chatFriendlyUrl]?: string
  [EAgentKeys.phoneStatus]?: EPhoneStatus
  [EAgentKeys.phoneVoice]?: string
  [EAgentKeys.phoneIntroRecording]?: string
  [EAgentKeys.textMethod]?: ETextMethodValues
  [EAgentKeys.scrapeStatus]?: EWebScrapeStatus
  [EAgentKeys.customInstructions]?: Array<object | TCustomInstruction> | null
  [EAgentKeys.escalations]?: TEscalation[] | []
  [EAgentKeys.chatStatus]?: EChatStatus
  [EAgentKeys.textStatus]?: ETextStatus
  [EAgentKeys.loopingAudio]?: ELoopingAudio
  [EAgentKeys.agentEmailCustom]?: string | null
  [EAgentKeys.outboundPhoneStatus]?: EOutboundPhoneStatus
  [EAgentKeys.agentTimezone]?: string
  [EAgentKeys.customAgentRules]?: string[] | null
}

export enum EImageType {
  logoImg = 'logoImg',
  chatbotImg = 'chatbotImg',
}

export type TAgentPhoneNumber = {
  phoneNumber: string
}

export type TAgentPhoneNumberError = {
  errorCode: number
  errorMessage: string
}

export type TCreateAgentPayload = Omit<TAgent, 'id'> & { partnerId: string }

export type TAgentChatbot = {
  id: string
  createdDate: Date
  updatedDate: Date
  agentId: string
  layout: TChatbotSettingsLayout
  legacyChatbotId: string
}

export type TCreateChatbotSettingsPayload = {
  agentId: string
  layout: TChatbotSettingsLayout
}

export type TCachedAgent = {
  data: TAgent
}

export type TAgentEmailSignature = {
  isDefaultSignature: boolean
  signature: string
}

export const personalityMapping = {
  [EPersonalityFriendlyName.professional]: EPersonalityValues.professional,
  [EPersonalityFriendlyName.jokey]: EPersonalityValues.jokey,
  [EPersonalityFriendlyName.enthusiastic]: EPersonalityValues.enthusiastic,
  [EPersonalityFriendlyName.laidBack]: EPersonalityValues.laidBack,
}

export type TAssistantTableData = Pick<
  TAgent,
  | EAgentKeys.id
  | EAgentKeys.agentReferenceName
  | EAgentKeys.phoneNumber
  | EAgentKeys.agentEmail
  | EAgentKeys.agentEmailCustom
  | EAgentKeys.phoneStatus
  | EAgentKeys.emailStatus
  | EAgentKeys.chatStatus
  | EAgentKeys.chatFriendlyUrl
>

export type TCreateAssistant = {
  [EAgentKeys.agentReferenceName]: string
  [EAgentKeys.service]: string
  [EAgentKeys.businessName]?: string
}

export type TAgentPhoneSettings = {
  id: string
  agentId: string
  keywords: string[]
  outageResponse: string
  patience: number
  noiseTolerance: number
  createdDate: Date
  updatedDate: Date
  phoneRules: string[]
  backupNumber: string
  textFollowUpClosing: string
}
